/**
 * API Service for making authenticated calls to the backend
 */

import { getMsalInstance } from '../auth/bootstrap'
import { getTokenRequest } from '../auth/authConfig'

/**
 * Extra headers injected into every authenticated request (e.g. demo mode header)
 */
let extraHeaders: Record<string, string> = {}

export function setExtraHeaders(headers: Record<string, string>) {
  extraHeaders = headers
}

export function getExtraHeaders(): Record<string, string> {
  return extraHeaders
}

/**
 * Base URL for API calls
 * Defaults to production URL if not specified in environment
 */
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://autopilot.sfi.microsoft.com'

/**
 * Standard API response wrapper
 */
export type ApiResponse<T> = {
  data: T | null
  error: string | null
  status: number
}

/**
 * Generic message response type
 */
export type MessageResponse = {
  message: string
}

/**
 * Acquires an access token for API calls
 * Attempts silent acquisition first, falls back to popup if needed
 * @returns The access token string
 * @throws Error if no active account or token acquisition fails
 */
export async function getAccessToken(): Promise<string> {
  const msalInstance = getMsalInstance()
  const account = msalInstance.getActiveAccount()

  if (!account) {
    throw new Error('No active account. Please sign in.')
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...getTokenRequest(),
      account,
    })
    return response.accessToken
  } catch {
    // Silent token acquisition failed, try popup
    const response = await msalInstance.acquireTokenPopup(getTokenRequest())
    return response.accessToken
  }
}

/**
 * Makes an authenticated fetch request to the API
 * @param endpoint - The API endpoint (should start with /api/ for proper routing)
 * @param options - Optional fetch options
 * @returns ApiResponse with data or error
 */
export async function authenticatedFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const accessToken = await getAccessToken()

    // In development, use relative paths (Vite proxies /api/* to backend)
    // In production, construct full URL
    const url = import.meta.env.DEV ? endpoint : new URL(endpoint, API_BASE_URL).toString()
    const response = await fetch(url, {
      ...options,
      headers: {
        ...extraHeaders,
        ...options.headers,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      return {
        data: null,
        error: errorText || `Request failed with status ${response.status}`,
        status: response.status,
      }
    }

    const data = (await response.json()) as T
    return {
      data,
      error: null,
      status: response.status,
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
    return {
      data: null,
      error: errorMessage,
      status: 0,
    }
  }
}

/**
 * Custom error class that preserves the HTTP status code from API responses
 */
export class ApiError extends Error {
  public readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}
